import { useEffect, useState, useCallback } from "react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { PRIORITY_META, IMPACT_LABEL, ARCHIVE_REASON_LABEL } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Gauge, CheckCircle2, Circle, PlayCircle } from "lucide-react";

const COLUMNS = [
  { key: "todo", label: "À faire", icon: Circle },
  { key: "in_progress", label: "En cours", icon: PlayCircle },
  { key: "done", label: "Terminé", icon: CheckCircle2 },
];

export default function ActionPlan() {
  const { current } = useEstablishments();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      const [{ data }, s] = await Promise.all([
        api.get(`/establishments/${current.id}/tasks`, { params: { include_archived: true } }),
        api.get(`/establishments/${current.id}/plan-summary`),
      ]);
      setTasks(data); setSummary(s.data);
    } finally { setLoading(false); }
  }, [current]);

  useEffect(() => { load(); }, [load]);

  const move = async (task, status) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try { await api.patch(`/tasks/${task.id}`, { status }); }
    catch (e) { toast.error("Erreur"); load(); }
  };

  if (loading) return <div className="p-8 grid place-items-center min-h-[60vh]"><Gauge className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  const live = tasks.filter((t) => t.status !== "archived");
  const archived = tasks.filter((t) => t.status === "archived");
  const done = live.filter((t) => t.status === "done").length;
  const pct = live.length ? Math.round((done / live.length) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Plan d'action</h1>
      <p className="text-slate-500 mt-1">Transformez chaque recommandation en action concrète.</p>

      {summary && (
        <div className="mt-5 rounded-2xl bg-blue-50 border border-blue-100 px-5 py-4 text-sm text-blue-900" data-testid="plan-summary">
          <b>{summary.improvements_detected}</b> amélioration{summary.improvements_detected > 1 ? "s" : ""} détectée{summary.improvements_detected > 1 ? "s" : ""} par l'audit — <b>{summary.priority_actions}</b> action{summary.priority_actions > 1 ? "s" : ""} prioritaire{summary.priority_actions > 1 ? "s" : ""} mise{summary.priority_actions > 1 ? "s" : ""} en avant (badge « Prioritaire »).
          <span className="block text-xs text-blue-700/80 mt-1">Chaque amélioration détectée correspond à une seule tâche (un problème = une action, même s'il est repéré par plusieurs analyses). Les actions prioritaires sont choisies par gravité (Critique puis Important), puis impact, portée, pertinence sectorielle et difficulté. {summary.archived > 0 && `${summary.archived} tâche(s) archivée(s) car devenue(s) non pertinente(s).`}</span>
        </div>
      )}

      <div className="mt-5 bg-white rounded-2xl p-5 border border-slate-200" data-testid="plan-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">{done} action{done > 1 ? "s" : ""} sur {live.length} terminée{done > 1 ? "s" : ""}</span>
          <span className="font-heading font-bold text-blue-600">{pct}%</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        {COLUMNS.map((col) => {
          const items = live.filter((t) => t.status === col.key);
          return (
            <div key={col.key} className="bg-slate-100/60 rounded-2xl p-4" data-testid={`plan-column-${col.key}`}>
              <div className="flex items-center gap-2 mb-4 px-1">
                <col.icon className="w-4 h-4 text-slate-500" />
                <h2 className="font-heading font-semibold text-slate-700">{col.label}</h2>
                <span className="ml-auto text-xs font-semibold text-slate-400 bg-white px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((t) => {
                  const meta = PRIORITY_META[t.priority] || PRIORITY_META.NON_MESURE;
                  return (
                    <div key={t.id} className={`bg-white rounded-xl p-4 border shadow-sm ${t.is_priority && t.status !== "done" ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"}`} data-testid={`task-card-${t.id}`}>
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>{t.pillar_name}</span>
                        {t.is_priority && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white" data-testid={`task-priority-badge-${t.id}`}>Prioritaire n°{t.rank}</span>}
                        {(t.sources || []).length > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" title={(t.check_ids || []).join(", ")}>{t.sources.length} analyses</span>}
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-800 leading-snug">{t.title}</p>
                      {t.problem && <p className="mt-1 text-xs text-slate-500" data-testid={`task-problem-${t.id}`}>Problème : {t.problem}</p>}
                      {t.why && <p className="mt-1 text-[11px] text-blue-700/80">Pourquoi : {t.why}</p>}
                      {t.correction && <pre className="mt-1.5 text-[11px] bg-slate-50 border border-slate-100 rounded-lg p-2 whitespace-pre-wrap font-mono text-slate-700 max-h-32 overflow-auto" data-testid={`task-correction-${t.id}`}>{t.correction}</pre>}
                      {t.pages?.length > 0 && <p className="mt-0.5 text-[11px] font-mono text-blue-700 truncate" title={t.pages.join("\n")}>Page : {t.pages[0]}{t.pages.length > 1 ? ` (+${t.pages.length - 1})` : ""}</p>}
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time}</span>
                        <span>· {IMPACT_LABEL[t.impact]}</span>
                        <span>· {t.difficulty}</span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        {col.key !== "todo" && (
                          <Button size="sm" variant="outline" onClick={() => move(t, col.key === "done" ? "in_progress" : "todo")} data-testid={`task-back-${t.id}`} className="text-xs h-7 rounded-lg">←</Button>
                        )}
                        {col.key !== "done" && (
                          <Button size="sm" onClick={() => move(t, col.key === "todo" ? "in_progress" : "done")} data-testid={`task-advance-${t.id}`} className="text-xs h-7 rounded-lg bg-blue-600 hover:bg-blue-700 flex-1">
                            {col.key === "todo" ? "Commencer" : "Terminer"}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Aucune tâche</p>}
              </div>
            </div>
          );
        })}
      </div>

      {archived.length > 0 && (
        <div className="mt-6" data-testid="archived-section">
          <button onClick={() => setShowArchived((v) => !v)} className="text-sm text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline" data-testid="toggle-archived-button">
            {showArchived ? "Masquer" : "Afficher"} l'historique des tâches archivées ({archived.length})
          </button>
          {showArchived && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {archived.map((t) => (
                <div key={t.id} className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-sm text-slate-500" data-testid={`archived-task-${t.id}`}>
                  <p className="font-medium text-slate-600 line-through">{t.title}</p>
                  <p className="text-xs mt-1">{ARCHIVE_REASON_LABEL[t.archive_reason] || t.archive_reason}{t.archived_at ? ` · ${new Date(t.archived_at).toLocaleDateString("fr-FR")}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
